import * as yup from "yup";
import { FilterDefinition } from "components/Shared/FilterEditor/utils";
import {
  createMakeGetManyFilterResult,
  getDateFilterDefinition,
  getDateTimeFilterDefinition,
  getIntegerFilterDefinition,
  getSearchFilterDefinition,
  getSelectFilterDefinition,
  getStringSelectFilterDefinition,
  SEARCH_FILTER_ID,
} from "components/Shared/FilterEditor/standardFilters";
import {
  BrandRetailerFilter,
  BrandRetailerStatus,
} from "__generated__/gql/graphql";
import { getEnumSchema } from "utils/schemas/utils";
import EnumSelect from "components/Shared/Select/EnumSelect";
import { BRAND_RETAILER_STATUS_LABELS } from "components/Shared/BrandRetailerStatus/utils";
import brandFilterDefinitions from "components/Admin/Brands/Manage/Table/filterDefinitions";
import getRetailerFilterDefinitions from "components/Shared/RetailerDirectory/filterDefinitions";
import createNestedFilterDefinitions from "components/Shared/FilterEditor/createNestedFilterDefinitions";
import OrderingPlatformSelect from "components/Shared/Select/OrderingPlatformSelect";
import ProductLineSelect from "components/Shared/Select/ProductLineSelect";
import SelectSkuMenu from "components/Shared/Select/SelectSkuMenu";
import DistributionCenterSelect from "components/Shared/Select/DistributionCenterSelect";
import UserSelect, {
  UserForBrandSelect,
} from "components/Shared/Select/UserSelect";
import { makeEnsureType } from "utils/typescriptUtilis";
import {
  SINGLE_STATUS_OPTIONS,
  STATUS_TABS_FILTER_NAME,
  StatusFilter,
} from "components/Retailers/Index/StatusFilterTabs";

const filterObj = makeEnsureType<BrandRetailerFilter>();

const makeGetManyResult = createMakeGetManyFilterResult<BrandRetailerFilter>();

export function getFilterDefinitions(isAdmin = false) {
  const brandFilters = createNestedFilterDefinitions(
    "brand",
    brandFilterDefinitions,
    { groupLabel: "Brand" },
  );

  const retailerFilterDefs = createNestedFilterDefinitions(
    "retailer",
    getRetailerFilterDefinitions(isAdmin),
    { groupLabel: "Retailer" },
  );

  const brandsSearchFilter = isAdmin
    ? brandFilters.find((fd) => fd.id.endsWith(SEARCH_FILTER_ID))
    : undefined;

  const retailersSearchFilter = retailerFilterDefs.find((fd) =>
    fd.id.endsWith(SEARCH_FILTER_ID),
  );

  const filterDefinitions: FilterDefinition[] = [
    getSearchFilterDefinition({
      getResult: (search, context) => {
        const ors: BrandRetailerFilter[] = [];

        if (brandsSearchFilter) {
          const result = brandsSearchFilter.getResult?.(search, context);
          if (result) ors.push(result);
        }

        if (retailersSearchFilter) {
          const result = retailersSearchFilter.getResult?.(search, context);
          if (result) ors.push(result);
        }

        if (!ors.length) return;
        if (ors.length === 1) return ors[0];
        return filterObj({ or: ors });
      },
    }),
    {
      id: STATUS_TABS_FILTER_NAME,
      label: "Status",
      isHidden: true,
      defaultVariant: "equalTo",
      equalityVariant: "equalTo",
      variants: [{ id: "equalTo", label: "Is" }],
      schema: getEnumSchema<StatusFilter>(StatusFilter),
      getResult: (value): BrandRetailerFilter | undefined => {
        if (value === null) return;

        if (value === StatusFilter.ALL) return;

        if (value === StatusFilter.ACTIVE) {
          return { status: { notIn: [BrandRetailerStatus.INACTIVE] } };
        }

        const singleStatus = SINGLE_STATUS_OPTIONS[value as StatusFilter];
        if (singleStatus) {
          return { status: { equalTo: singleStatus } };
        }
      },
    },
    getSelectFilterDefinition({
      id: "status",
      label: "Status",
      getSelectResult: (
        value,
        { filterVariantDefinition: { id: variantId } },
      ) => ({ status: { [variantId]: value } }),
      selectSchema: getEnumSchema<BrandRetailerStatus>(BrandRetailerStatus),
      renderSelectInput: ({ field }, { isMulti }) => (
        <EnumSelect
          {...field}
          isMulti={isMulti}
          menuPortalTarget={document.body}
          enum={BrandRetailerStatus}
          labelsByValue={BRAND_RETAILER_STATUS_LABELS}
        />
      ),
    }),
    getIntegerFilterDefinition({
      id: "numStores",
      label: "Store Count",
      defaultVariant: "greaterThan",
    }),
    getSelectFilterDefinition({
      id: "accountOwner",
      label: "Account Owner",
      getSelectResult: (
        value,
        { filterVariantDefinition: { id: variantId } },
      ) => ({ accountOwnerId: { [variantId]: value } }),
      selectSchema: yup.string().uuid(),
      renderSelectInput: ({ field }, { isMulti }) =>
        isAdmin ? (
          <UserSelect
            {...field}
            isAdmin
            isMulti={isMulti}
            menuPortalTarget={document.body}
          />
        ) : (
          <UserForBrandSelect
            {...field}
            isMulti={isMulti}
            menuPortalTarget={document.body}
          />
        ),
    }),
    getStringSelectFilterDefinition({
      id: "distributor",
      label: "Distributor",
      getResult: makeGetManyResult("brandRetailerDistributionCenters", (f) => ({
        distributionCenter: { orderingPlatform: { name: f } },
      })),
      getSelectResult: makeGetManyResult(
        "brandRetailerDistributionCenters",
        (f) => ({ distributionCenter: { orderingPlatformId: f } }),
      ),
      selectSchema: yup.string().uuid(),
      renderSelectInput: ({ field }, { isMulti }) => (
        <OrderingPlatformSelect
          {...field}
          isAdmin={isAdmin}
          isAddedToBrand={!isAdmin}
          isMulti={isMulti}
          menuPortalTarget={document.body}
        />
      ),
    }),
    getStringSelectFilterDefinition({
      id: "distributionCenter",
      label: "Distribution Center",
      getResult: makeGetManyResult("brandRetailerDistributionCenters", (f) => ({
        distributionCenter: { name: f },
      })),
      getSelectResult: makeGetManyResult(
        "brandRetailerDistributionCenters",
        (f) => ({ distributionCenterId: f }),
      ),
      selectSchema: yup.string().uuid(),
      renderSelectInput: ({ field }, { isMulti }) => (
        <DistributionCenterSelect
          {...field}
          isAdmin={isAdmin}
          isAddedToBrand={!isAdmin}
          isMulti={isMulti}
          menuPortalTarget={document.body}
          filter={{ name: { isNull: false } }}
        />
      ),
    }),
  ];

  if (!isAdmin) {
    filterDefinitions.push(
      getStringSelectFilterDefinition({
        id: "productLine",
        label: "Product Line",
        getResult: makeGetManyResult("brandRetailerSkus", (f) => ({
          sku: { productLine: { name: f } },
        })),
        getSelectResult: makeGetManyResult("brandRetailerSkus", (f) => ({
          sku: { productLineId: f },
        })),
        selectSchema: yup.string().uuid(),
        renderSelectInput: ({ field }, { isMulti }) => (
          <ProductLineSelect
            {...field}
            isMulti={isMulti}
            menuPortalTarget={document.body}
          />
        ),
      }),
      getStringSelectFilterDefinition({
        id: "sku",
        label: "SKU",
        getResult: makeGetManyResult("brandRetailerSkus", (f) => ({
          sku: { name: f },
        })),
        getSelectResult: makeGetManyResult("brandRetailerSkus", (f) => ({
          sku: { id: f },
        })),
        selectSchema: yup.string().uuid(),
        renderSelectInput: ({ field }, { isMulti }) => (
          <SelectSkuMenu
            {...field}
            isMulti={isMulti}
            menuPortalTarget={document.body}
          />
        ),
      }),
    );
  }

  filterDefinitions.push(
    getDateFilterDefinition({ id: "startDate", label: "Start Date" }),
    getDateFilterDefinition({ id: "endDate", label: "End Date" }),
    getDateTimeFilterDefinition({
      id: "lastStatusUpdateAt",
      label: "Last Status Update",
      getResult: (value, { filterVariantDefinition: { id: variantId } }) =>
        filterObj({
          brandRetailerComments: {
            aggregates: { max: { createdAt: { [variantId]: value } } },
          },
        }),
    }),
  );

  if (isAdmin) {
    filterDefinitions.push(...brandFilters);
  }

  filterDefinitions.push(...retailerFilterDefs);

  return filterDefinitions;
}

const filterDefinitions = getFilterDefinitions();

export default filterDefinitions;
